/**
 * King Living QuantityPicker Theme
 *
 * Global quantity picker styling system:
 * - Enabled state: white background, gray border, brown plus button
 * - Disabled state: light gray background, muted colors
 * - Rounded container with minus, input, and plus buttons
 * - Buttons: 40px × 40px with 12px horizontal padding
 * - Input: 40px × 40px
 *
 * Structure supports:
 * - baseStyle: Base styles for all parts
 * - variants: Different visual styles (default, compact, etc.)
 * - sizes: Different sizes (sm, md, lg)
 * - defaultProps: Default variant and size
 *
 * @override Extends Chakra UI theme
 *
 * @example Adding a new variant:
 * ```javascript
 * variants: {
 *   default: (props) => ({ ... }),
 *   compact: (props) => ({
 *     container: { padding: '2px' },
 *     decrementButton: { height: '32px', width: '32px' },
 *     input: { height: '32px', width: '32px' },
 *     incrementButton: { height: '32px', width: '32px' },
 *   }),
 * }
 * ```
 *
 * @example Adding a new size:
 * ```javascript
 * sizes: {
 *   sm: {
 *     decrementButton: { height: '32px', width: '32px' },
 *     input: { height: '32px', width: '32px' },
 *     incrementButton: { height: '32px', width: '32px' },
 *   },
 * }
 * ```
 *
 * @example Overriding base styles:
 * ```javascript
 * // In a child package, you can override specific parts:
 * const CustomQuantityPicker = {
 *   ...baseQuantityPicker,
 *   baseStyle: (props) => ({
 *     ...baseQuantityPicker.baseStyle(props),
 *     container: {
 *       ...baseQuantityPicker.baseStyle(props).container,
 *       borderRadius: '8px', // Override border radius
 *     },
 *   }),
 * };
 * ```
 */

// Common button styles - extracted for reuse and easy override
const getButtonBaseStyles = () => ({
  _active: {bg: 'transparent'},
  _focus: {boxShadow: 'none', outline: 'none'},
  _hover: {bg: 'transparent'},
  bg: 'transparent',
  border: 'none',
  flexShrink: 0,
  fontSize: '16px',
  fontWeight: 'normal',
  lineHeight: '1.4',
  margin: 0,
  paddingInline: '12px',
  paddingY: 0,
});

// Common input styles - extracted for reuse and easy override
const getInputBaseStyles = () => ({
  _focus: {boxShadow: 'none', outline: 'none'},
  bg: 'transparent',
  border: 'none',
  flexShrink: 0,
  fontSize: '16px',
  fontWeight: '600',
  lineHeight: '1.4',
  margin: 0,
  paddingX: 0,
  paddingY: 0,
  textAlign: 'center',
  _disabled: {
    bg: 'transparent',
    color: 'brandColors.greyscale40',
    cursor: 'not-allowed',
    opacity: 1,
  },
});

// Common container styles - extracted for reuse and easy override
const getContainerBaseStyles = () => ({
  alignItems: 'center',
  border: '1px solid',
  borderColor: 'brandColors.greyscale10',
  borderRadius: '4px',
  display: 'flex',
  gap: '0',
  margin: 0,
  padding: 0,
  width: 'fit-content',
});

const QuantityPicker = {
  parts: ['container', 'decrementButton', 'input', 'incrementButton'],

  baseStyle: (props) => {
    const {isDisabled = false} = props;

    return {
      container: {
        ...getContainerBaseStyles(),
        bg: isDisabled ? 'brandColors.greyscale10' : 'white',
      },
      decrementButton: {
        ...getButtonBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.greyscale50',
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
      input: {
        ...getInputBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.greyscale90',
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
      incrementButton: {
        ...getButtonBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.primaryDarkLinen',
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
    };
  },

  // Variants for different visual styles
  variants: {
    default: (props) => {
      // Default variant - same as baseStyle
      return {};
    },
    // Future variants can be added here:
    // compact: (props) => ({ ... }),
    // outlined: (props) => ({ ... }),
  },

  // Sizes for different dimensions
  sizes: {
    md: {
      // Default size - 40px
      container: {},
      decrementButton: {
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
      input: {
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
      incrementButton: {
        height: '40px',
        maxHeight: '40px',
        maxWidth: '40px',
        minHeight: '40px',
        minWidth: '40px',
        width: '40px',
      },
    },
    // Future sizes can be added here:
    // sm: { ... },
    // lg: { ... },
  },

  // Default props
  defaultProps: {
    variant: 'default',
    size: 'md',
  },
};

export default QuantityPicker;
